import {toast} from "react-toastify";
import {useSelector} from "react-redux";
import {Button, Form, Modal} from "react-bootstrap";
import {FormProvider, useForm} from "react-hook-form";
import React, {cloneElement, Fragment, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import IconButton from "../../../common/IconButton";
import EventBus from "../../../../app/eventbus/EventBus";
import billingService from "../../../../services/billingService";
import OrganizationPackageDiscountForm from "../form/OrganizationPackageDiscountForm";
import {getBillingErrorMessage, getBillingErrorStatus} from "../../../../helpers/billing-response";
import {selectLang, Translate} from "../../../../app/store/reducers/main/mainReducer";
import {
    ADD_ORGANIZATION_PACKAGE_DISCOUNT_FAILED,
    ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED
} from "../../../../app/eventbus/billingEvents";

/**
 * Tashkilotga paket chegirmasi qo'shish modali.
 * Muvaffaqiyatdan so'ng ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED eventi yuboriladi.
 */
const AddOrganizationPackageDiscountModal = ({organizationId, packages, disabledPackageCodes, children, ...props}) => {
    const [show, setShow] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const lang = useSelector(selectLang);
    const t = Translate;

    const methods = useForm({
        defaultValues: {
            packageCode: null,
            amount: '',
        },
    });

    const handleShow = () => setShow(true);
    const handleClose = () => {
        methods.reset();
        setShow(false);
    };

    const onSubmit = (formData) => {
        const payload = {
            organization_id: organizationId,
            package_code: formData.packageCode,
            amount: Number(formData.amount),
        };

        setIsLoading(true);
        billingService.createOrganizationPackageDiscount(payload)
            .then((discount) => {
                EventBus.dispatch(ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, discount);
                toast.success(t(lang, 'common.toast.success'));
                handleClose();
            })
            .catch((error) => {
                EventBus.dispatch(ADD_ORGANIZATION_PACKAGE_DISCOUNT_FAILED, error);

                const status = getBillingErrorStatus(error);
                const message = getBillingErrorMessage(error);

                if (status === 409) {
                    methods.setError('packageCode', {
                        type: 'server',
                        message: t(lang, 'admin.organization.package_discounts.error.already_exists')
                    });
                    toast.error(t(lang, 'admin.organization.package_discounts.error.already_exists'));
                } else if (status === 422) {
                    methods.setError('amount', {
                        type: 'server',
                        message: t(lang, 'admin.organization.package_discounts.error.invalid_amount')
                    });
                    toast.error(t(lang, 'admin.organization.package_discounts.error.invalid_amount'));
                } else if (status === 404) {
                    toast.error(t(lang, 'admin.organization.package_discounts.error.not_found'));
                } else {
                    toast.error(message || t(lang, 'common.toast.error'));
                }
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Fragment>
            {!!children ? cloneElement(children, {...children.props, onClick: handleShow}) : (
                <IconButton variant={'primary'} size={'sm'} onClick={handleShow} icon={'plus'} {...props}>
                    {t(lang, 'admin.organization.package_discounts.add_modal.show_button')}
                </IconButton>
            )}

            <Modal show={show} onHide={handleClose} size={'lg'}>
                <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(onSubmit)}>
                        <Modal.Header closeButton={true}>
                            <Modal.Title>{t(lang, 'admin.organization.package_discounts.add_modal.title')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <OrganizationPackageDiscountForm isEditing={false}
                                                             packages={packages}
                                                             disabledPackageCodes={disabledPackageCodes}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant={'secondary'} type={'button'} onClick={handleClose}>
                                {t(lang, 'common.button.cancel')}
                            </Button>
                            <Button variant={'primary'} type={'submit'} disabled={isLoading}>
                                {isLoading && <FontAwesomeIcon pulse={true} className="me-2" icon="spinner"/>}
                                {t(lang, 'items.common.add')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </FormProvider>
            </Modal>
        </Fragment>
    );
};

export default AddOrganizationPackageDiscountModal;
